import React, { useState, useEffect } from 'react';
import { getProductImage } from '../../utils/imageHelpers';
import { formatCouponDiscount } from '../../utils/couponHelpers';
import { getFinalPrice, hasDiscount as isDiscounted } from '../../utils/priceHelpers';

const CheckoutModal = ({
  cart,
  getCartTotal,
  deliverySlots,
  selectedSlot,
  setSelectedSlot,
  paymentMethod,
  setPaymentMethod,
  onClose,
  onModifyCart,
  processPayment,
  deliveryAddresses,
  setShowAddAddress,
  handleSetDefaultAddress,
  selectedAddressId,
  setSelectedAddressId,
  appliedCoupon,
  couponDiscount,
  couponCode = '',
  setCouponCode,
  couponError = '',
  handleApplyCoupon,
  handleRemoveCoupon,
  getSuggestedCoupons,
  getFinalTotal,
  appSettings = { taxRate: 5, freeShippingThreshold: 500, standardShippingFee: 50, razorpayEnabled: false, payuEnabled: false, codEnabled: false },
  walletBalance = 0,
  onWalletUsed,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
}) => {
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmountInput, setWalletAmountInput] = useState('');

  const orderTotal = getFinalTotal ? getFinalTotal() : getCartTotal();
  const maxWalletUse = Math.min(walletBalance, orderTotal);
  const walletAmountToUse = useWallet ? Math.min(parseFloat(walletAmountInput) || maxWalletUse, maxWalletUse) : 0;
  const amountDue = Math.max(0, orderTotal - walletAmountToUse);

  // Auto-enable wallet when it fully covers the order
  useEffect(() => {
    if (walletBalance >= orderTotal && orderTotal > 0) {
      setUseWallet(true);
      setWalletAmountInput(String(orderTotal));
    }
  }, [walletBalance, orderTotal]);

  // Auto-reset payment method when its gateway gets disabled
  useEffect(() => {
    const isPayuMethod     = ['upi','card','wallet','netbanking'].includes(paymentMethod);
    const isRazorpayMethod = paymentMethod === 'razorpay';
    const isCodFamily      = paymentMethod === 'cod' || paymentMethod === 'upi_cod';

    if (isRazorpayMethod && !appSettings.razorpayEnabled) {
      // Razorpay selected but disabled — fall back
      if (appSettings.payuEnabled !== false) setPaymentMethod('upi');
      else if (appSettings.codEnabled !== false) setPaymentMethod('cod');
    } else if (isPayuMethod && !appSettings.payuEnabled) {
      // PayU method selected but disabled — fall back to Razorpay or COD
      if (appSettings.razorpayEnabled) setPaymentMethod('razorpay');
      else if (appSettings.codEnabled !== false) setPaymentMethod('cod');
    } else if (isCodFamily && appSettings.codEnabled === false) {
      // COD disabled — fall back to Razorpay or PayU
      if (appSettings.razorpayEnabled) setPaymentMethod('razorpay');
      else if (appSettings.payuEnabled !== false) setPaymentMethod('upi');
    }
  }, [appSettings.razorpayEnabled, appSettings.payuEnabled, appSettings.codEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleWallet = (checked) => {
    setUseWallet(checked);
    if (checked) setWalletAmountInput(String(maxWalletUse));
    else setWalletAmountInput('');
  };

  const validAddresses = Array.isArray(deliveryAddresses)
    ? deliveryAddresses.filter(a => a.address && a.address.trim())
    : [];
  const hasAddress = !!selectedAddressId && validAddresses.some(a => a.id === selectedAddressId);

  const handleConfirmPay = () => {
    if (!hasAddress) {
      alert('Please add and select a delivery address before placing your order.');
      return;
    }
    processPayment({ walletAmountToUse });
    // Only optimistically deduct for partial wallet use; full-wallet orders are handled inside processPayment
    if (walletAmountToUse > 0 && walletAmountToUse < orderTotal && onWalletUsed) onWalletUsed(walletAmountToUse);
  };

    return (
    <div className="checkout-overlay fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-[300] p-0 md:p-4 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md md:max-w-lg mx-auto rounded-t-3xl md:rounded-3xl max-h-[90vh] overflow-y-auto md:shadow-2xl" style={{
        backgroundImage: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
      }}>
       {/* Header with gradient background */}
       <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-500 p-6 rounded-t-3xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">Complete Order</h2>
              <p className="text-blue-100 text-sm mt-1">Fast & Secure Checkout</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all text-white">
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
        {/* Cart Total Card - More Attractive */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-4 mb-6 shadow-sm">
          <p className="text-blue-600 text-sm font-semibold uppercase tracking-wider mb-1">Order Value</p>
          <div className="flex justify-between items-end">
            <span className="text-gray-700 font-medium">Cart Total</span>
            <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent text-3xl font-bold">₹{getCartTotal()}</span>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">📍</span>
            <h3 className="font-bold text-gray-800 text-lg">Delivery Address</h3>
          </div>
          <div className="space-y-3 mb-4">
            {validAddresses.length > 0 ? (
              validAddresses.map(addr => (
                <div
                  key={addr.id}
                  onClick={() => setSelectedAddressId && setSelectedAddressId(addr.id)}
                  className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-102 ${selectedAddressId === addr.id ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md' : (addr.isDefault ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm')}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-bold text-gray-800 flex items-center gap-2">
                        {addr.label === 'Work' ? '💼' : '🏠'} {addr.label} 
                        {addr.isDefault && <span className="text-xs text-blue-600 font-semibold bg-blue-100 px-2 py-1 rounded-full">Default</span>}
                      </p>
                      <p className="text-sm text-gray-600 mt-2 font-medium">{addr.recipient_name} • {addr.phone}</p>
                      <p className="text-sm text-gray-500 mt-1">{addr.address}, {addr.city} {addr.pincode}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {selectedAddressId === addr.id && (
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center text-white text-xs shadow-md">✓</div>
                      )}
                      {!addr.isDefault && (
                        <button onClick={(e) => { e.stopPropagation(); handleSetDefaultAddress && handleSetDefaultAddress(addr.id); }} className="text-xs text-blue-600 font-semibold hover:bg-blue-100 px-2 py-1 rounded-lg transition-all">Set Default</button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 rounded-2xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100 text-center shadow-sm">
                <p className="text-orange-700 font-semibold text-sm">📭 No address added yet</p>
                <p className="text-orange-600 text-xs mt-1">Add a delivery address to continue with checkout</p>
              </div>
            )}
            <button onClick={() => setShowAddAddress && setShowAddAddress(true)} className="w-full py-3 text-sm text-blue-600 font-bold border-2 border-blue-600 rounded-xl hover:bg-blue-50 transition-all">+ Add New Address</button>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">⏰</span>
            <h3 className="font-bold text-gray-800 text-lg">Delivery Slot</h3>
          </div>
          <div className="space-y-3">
            {deliverySlots.map(slot => {
              const icon = slot.icon || '🕐';
              return (
                <button
                  key={slot.id}
                  onClick={() => setSelectedSlot(slot.id)}
                  className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between transform hover:scale-102 ${
                    selectedSlot === slot.id
                      ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{icon}</span>
                    <div className="text-left">
                      <p className="font-bold text-gray-800">{slot.label}</p>
                      <p className="text-sm text-gray-500">{slot.time}</p>
                    </div>
                  </div>
                  {selectedSlot === slot.id && (
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">📦</span>
              <h3 className="font-bold text-gray-800 text-lg">Order Summary</h3>
            </div>
            {onModifyCart && (
              <button onClick={onModifyCart} className="text-xs text-blue-600 font-bold underline hover:text-blue-700">
                Edit Items
              </button>
            )}
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 mb-6 space-y-3 border border-gray-200">
            {(cart || []).map((item, index) => {
              const unitLabel = item.unit ? ` ${item.unit}` : '';
              const weightLabel = item.weight ? `${item.weight}${unitLabel}` : '';
              const hasDiscount = isDiscounted(item);
              const priceValue = getFinalPrice(item);
              const originalTotal = Number(item.price || 0) * Number(item.quantity || 0);
              const saleTotal = priceValue * Number(item.quantity || 0);
              const savings = hasDiscount ? (Number(item.price) - priceValue) * Number(item.quantity) : 0;
              return (
                <div key={`${item.id}-${index}`} className="flex flex-col gap-1.5 border-b border-gray-300 last:border-b-0 pb-3 mb-2 last:pb-0 last:mb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2 rounded-lg bg-white shadow-sm flex-shrink-0">
                        {getProductImage(item, 'small')}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 text-sm">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {weightLabel ? `${weightLabel} • ` : ''}Qty: <span className="font-bold text-gray-700">{item.quantity}</span>
                        </p>
                      </div>
                    </div>
                    {/* Line total — the amount actually charged for this item */}
                    <div className="text-right min-w-[72px] flex-shrink-0">
                      <span className="text-sm font-bold text-gray-800 block">₹{saleTotal.toFixed(2)}</span>
                      {hasDiscount && (
                        <span className="text-[11px] text-gray-400 line-through block">₹{originalTotal.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  {/* Per-unit math on the left, savings on the right */}
                  <div className="flex items-center justify-between pl-16 text-xs">
                    <span className="text-gray-500">
                      ₹{Number(priceValue).toFixed(2)} <span className="text-gray-400">× {item.quantity}</span>
                    </span>
                    {hasDiscount && (
                      <span className="text-green-600 font-semibold">💚 You save ₹{savings.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Wallet section — appears above payment methods */}
          {walletBalance > 0 && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-2xl p-4 mb-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👛</span>
                  <div>
                    <p className="text-sm font-bold text-gray-800">Wallet Balance</p>
                    <p className="text-xs text-gray-600">Available: <span className="font-bold text-purple-600">₹{walletBalance.toFixed(2)}</span></p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={useWallet}
                    onChange={(e) => handleToggleWallet(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-purple-500 peer-checked:to-purple-600"></div>
                </label>
              </div>
              {useWallet && (
                <div className="mt-3 bg-white rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 text-sm font-semibold">₹</span>
                    <input
                      type="number"
                      min="1"
                      max={maxWalletUse}
                      value={walletAmountInput}
                      onChange={(e) => setWalletAmountInput(e.target.value)}
                      className="flex-1 border-2 border-purple-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500 font-semibold"
                    />
                    <button
                      onClick={() => setWalletAmountInput(String(maxWalletUse))}
                      className="text-xs text-purple-600 font-bold hover:bg-purple-100 px-2 py-1 rounded-lg transition-all"
                    >
                      MAX
                    </button>
                  </div>
                  {walletAmountToUse > 0 && (
                    <p className="text-xs text-green-600 mt-2 font-semibold flex items-center gap-1">
                      ✓ ₹{walletAmountToUse.toFixed(2)} will be deducted from wallet
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {amountDue <= 0 ? (
            /* Wallet covers the full order — no other payment needed */
            <div className="flex items-center gap-4 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-400 rounded-2xl p-4 mb-6 shadow-sm">
              <span className="text-3xl flex-shrink-0">👛</span>
              <div className="flex-1">
                <p className="font-bold text-green-700 text-sm">Paying with Wallet</p>
                <p className="text-xs text-green-600">No additional payment required ✓</p>
              </div>
              <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-white font-bold">✓</span>
              </div>
            </div>
          ) : (
            <>

              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">💳</span>
                <h3 className="font-bold text-gray-800 text-lg">Payment Method</h3>
              </div>
              <div className="space-y-3 mb-6">
                {/* COD controlled by appSettings.codEnabled */}
                {appSettings.codEnabled !== false && (
                  <>
                    <button
                      onClick={() => setPaymentMethod('cod')}
                      className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between transform hover:scale-102 ${
                        paymentMethod === 'cod'
                          ? 'border-green-500 bg-gradient-to-br from-green-50 to-green-100 shadow-md'
                          : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-3xl">💵</span>
                        <div className="text-left">
                          <p className="font-bold text-gray-800">Cash on Delivery</p>
                          <p className="text-sm text-gray-500">Pay when you receive</p>
                        </div>
                      </div>
                      {paymentMethod === 'cod' && (
                        <div className="w-6 h-6 bg-gradient-to-br from-green-600 to-green-500 rounded-full flex items-center justify-center shadow-md">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </button>

                    <button
                      onClick={() => setPaymentMethod('upi_cod')}
                      className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between transform hover:scale-102 ${
                        paymentMethod === 'upi_cod'
                          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100 shadow-md'
                          : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-3xl">📲</span>
                        <div className="text-left">
                          <p className="font-bold text-gray-800">UPI QR at Delivery</p>
                          <p className="text-sm text-gray-500">Scan UPI QR when delivered</p>
                        </div>
                      </div>
                      {paymentMethod === 'upi_cod' && (
                        <div className="w-6 h-6 bg-gradient-to-br from-purple-600 to-purple-500 rounded-full flex items-center justify-center shadow-md">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </button>

                    {/* Inline QR preview when UPI Scan on Delivery is selected */}
                    {paymentMethod === 'upi_cod' && (
                      <div className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4 flex flex-col items-center shadow-sm">
                        <p className="text-xs font-bold text-purple-700 mb-3 uppercase tracking-wide">UPI QR Code (shown at delivery)</p>
                        <img
                          src={upiQrCode}
                          alt="UPI QR Code"
                          style={{ width: 160, height: 160, borderRadius: 12, display: 'block', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                        {upiId && (
                          <p className="text-xs font-bold text-purple-800 mt-3 text-center bg-white px-3 py-1 rounded-lg">{upiId}</p>
                        )}
                        <p className="text-xs text-purple-600 mt-2 text-center font-medium">💡 Agent will scan this QR code</p>
                      </div>
                    )}
                  </>
                )}

                {/* Online payments (UPI / Card / Wallet) — all controlled by appSettings.payuEnabled */}
                {appSettings.payuEnabled !== false && (
                <>
                <button
                  onClick={() => setPaymentMethod('upi')}
                  className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between transform hover:scale-102 ${
                    paymentMethod === 'upi'
                      ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">📱</span>
                    <div className="text-left">
                      <p className="font-bold text-gray-800">UPI Payment</p>
                      <p className="text-sm text-gray-500">PhonePe, Google Pay, Paytm</p>
                    </div>
                  </div>
                  {paymentMethod === 'upi' && (
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between transform hover:scale-102 ${
                    paymentMethod === 'card'
                      ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-indigo-100 shadow-md'
                      : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">💳</span>
                    <div className="text-left">
                      <p className="font-bold text-gray-800">Card Payment</p>
                      <p className="text-sm text-gray-500">Visa, Mastercard, RuPay</p>
                    </div>
                  </div>
                  {paymentMethod === 'card' && (
                    <div className="w-6 h-6 bg-gradient-to-br from-indigo-600 to-indigo-500 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => setPaymentMethod('wallet')}
                  className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between transform hover:scale-102 ${
                    paymentMethod === 'wallet'
                      ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-orange-100 shadow-md'
                      : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">👛</span>
                    <div className="text-left">
                      <p className="font-bold text-gray-800">Digital Wallet</p>
                      <p className="text-sm text-gray-500">Paytm, Amazon Pay</p>
                    </div>
                  </div>
                  {paymentMethod === 'wallet' && (
                    <div className="w-6 h-6 bg-gradient-to-br from-orange-600 to-orange-500 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>
                </>
                )}

                {/* Razorpay — shown only when razorpay_enabled is true in admin settings */}
                {appSettings.razorpayEnabled && (
                  <button
                    onClick={() => setPaymentMethod('razorpay')}
                    className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between transform hover:scale-102 ${
                      paymentMethod === 'razorpay'
                        ? 'border-blue-600 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">⚡</span>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-gray-800">Razorpay</p>
                          <span className="text-[10px] bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full">SECURE</span>
                        </div>
                        <p className="text-sm text-gray-500">UPI · Card · Net Banking · Wallet</p>
                      </div>
                    </div>
                    {paymentMethod === 'razorpay' ? (
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0" />
                    )}
                  </button>
                )}

                {/* No payment options available */}
                {appSettings.codEnabled === false && appSettings.payuEnabled === false && !appSettings.razorpayEnabled && (
                  <div className="p-4 rounded-2xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100 text-center shadow-sm">
                    <p className="text-orange-700 font-bold text-sm">⚠️ No payment methods available</p>
                    <p className="text-orange-600 text-xs mt-1">Please contact support or try again later.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Shipping Motivation Message */}
        {(() => {
          const cartTotal = getCartTotal() - (couponDiscount || 0);
          const remaining = appSettings.freeShippingThreshold - cartTotal;
          
          if (cartTotal >= appSettings.freeShippingThreshold) {
            return (
              <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-2 border-green-400 rounded-2xl p-4 mb-6 flex items-center gap-3 shadow-sm">
                <div className="text-3xl animate-bounce">🎉</div>
                <div className="flex-1">
                  <p className="font-bold text-green-700 text-sm">Free Shipping Unlocked!</p>
                  <p className="text-xs text-green-600 mt-0.5">No delivery charges on this order</p>
                </div>
              </div>
            );
          } else if (remaining > 0) {
            const percentage = Math.round(((appSettings.freeShippingThreshold - remaining) / appSettings.freeShippingThreshold) * 100);
            return (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-400 rounded-2xl p-4 mb-6 shadow-sm">
                <div className="flex items-start gap-3 mb-2">
                  <div className="text-2xl flex-shrink-0">🚚</div>
                  <div className="flex-1">
                    <p className="font-bold text-blue-700 text-sm">₹{remaining.toFixed(0)} away from FREE shipping!</p>
                    <p className="text-xs text-blue-600 mt-0.5">Save ₹{appSettings.standardShippingFee} on delivery</p>
                  </div>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500" style={{width: `${percentage}%`}}></div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Coupon Section */}
        <div className="mb-6">
          {appliedCoupon ? (
            <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-300 rounded-2xl px-4 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎁</span>
                <div>
                  <p className="text-sm font-bold text-green-700">{appliedCoupon.code}</p>
                  <p className="text-xs text-green-600 font-semibold">You save ₹{couponDiscount}</p>
                </div>
              </div>
              <button
                onClick={handleRemoveCoupon}
                className="text-xs text-red-500 font-bold hover:text-red-600 px-3 py-1 rounded-lg hover:bg-red-50 transition-all"
              >
                Remove
              </button>
            </div>
          ) : (
            <div>
              {/* Manual input */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={couponCode}
                  onChange={e => setCouponCode && setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleApplyCoupon && handleApplyCoupon()}
                  placeholder="Enter coupon code"
                  className="flex-1 border-2 border-gray-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-blue-400 uppercase placeholder:normal-case placeholder:text-gray-400 font-semibold"
                />
                <button
                  onClick={handleApplyCoupon}
                  className="px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-bold rounded-2xl hover:from-blue-700 hover:to-blue-600 transition-all whitespace-nowrap shadow-md"
                >
                  Apply
                </button>
              </div>
              {couponError && (
                <p className="text-xs text-red-500 mb-3 ml-1 font-semibold">❌ {couponError}</p>
              )}

              {/* Eligible coupons list */}
              {getSuggestedCoupons && (() => {
                const suggested = getSuggestedCoupons();
                if (!suggested || suggested.length === 0) return null;
                return (
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">Available Offers</p>
                    <div className="space-y-2">
                      {suggested.map(coupon => {
                        const eligible = getCartTotal() >= coupon.minOrder;
                        const shortfall = coupon.minOrder - getCartTotal();
                        return (
                          <div
                            key={coupon.code}
                            className={`border-2 rounded-2xl p-3 flex items-center justify-between gap-3 transition-all ${eligible ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-blue-100' : 'border-gray-200 bg-gray-50 opacity-60'}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-bold text-sm text-blue-700 tracking-wide">{coupon.code}</span>
                                <span className="bg-gradient-to-r from-blue-200 to-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full">
                                  {formatCouponDiscount(coupon)}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 truncate">
                                {eligible
                                  ? coupon.description
                                  : `Add ₹${shortfall.toFixed(0)} to unlock`}
                              </p>
                            </div>
                            <button
                              disabled={!eligible}
                              onClick={() => handleApplyCoupon && handleApplyCoupon(coupon.code)}
                              className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${eligible ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            >
                              {eligible ? 'Apply' : 'Locked'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Price Breakdown */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 mb-6 border border-gray-200">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-semibold">Subtotal</span>
              <span className="font-bold text-gray-800">₹{getCartTotal()}</span>
            </div>
            {appliedCoupon && couponDiscount > 0 && (
              <div className="flex justify-between items-center text-green-700">
                <span className="font-semibold">Coupon ({appliedCoupon.code})</span>
                <span className="font-bold">-₹{couponDiscount}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-semibold">Shipping</span>
              <span className="font-bold">
                {getCartTotal() >= appSettings.freeShippingThreshold ? (
                  <span className="text-green-600">Free 🎉</span>
                ) : (
                  `₹${appSettings.standardShippingFee}`
                )}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-semibold">Tax (GST {appSettings.taxRate}%)</span>
              <span className="font-bold text-gray-800">₹{(getCartTotal() * (appSettings.taxRate / 100)).toFixed(2)}</span>
            </div>
            {walletAmountToUse > 0 && (
              <div className="flex justify-between items-center text-purple-700">
                <span className="font-semibold">Wallet Deduction</span>
                <span className="font-bold">-₹{walletAmountToUse.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t-2 border-gray-300 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-800">
                  {walletAmountToUse > 0 ? 'Amount Due' : 'Total'}
                </span>
                <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                  ₹{amountDue.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Coupon Section - REPLACED WITH NEW IMPROVED VERSION ABOVE */}

        {/* Price Breakdown - REPLACED WITH NEW IMPROVED VERSION ABOVE */}

        {!hasAddress && (
          <p className="text-orange-600 text-sm text-center mb-3 font-semibold text-base">
            📍 Add a delivery address to continue
          </p>
        )}
        {validAddresses.length > 0 && !hasAddress && (
          <p className="text-red-600 text-sm text-center mb-3 font-semibold">⚠️ Please select a delivery address</p>
        )}
        
        <button
          onClick={handleConfirmPay}
          disabled={!hasAddress || (appSettings.codEnabled === false && appSettings.payuEnabled === false && !appSettings.razorpayEnabled)}
          className={`w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl ${
            !hasAddress || (appSettings.codEnabled === false && appSettings.payuEnabled === false)
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600 transform hover:scale-105 active:scale-95'
          }`}>
          {amountDue <= 0
            ? '✓ Confirm & Pay with Wallet'
            : `Confirm & Pay ₹${amountDue.toFixed(2)}`}
        </button>

        {onModifyCart && (
          <button
            onClick={onModifyCart}
            className="w-full py-3 mt-3 rounded-2xl font-bold text-base border-2 border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all">
            ✏️ Edit Items
          </button>
        )}
      </div>
      </div>
    </div>
  );
};

export default CheckoutModal;